// src/screens/TermsOfService.js
import React from "react";
import "../styles/LegalPage.css";

export default function TermsOfService() {
  return (
    <div className="legal-page">
      <div className="legal-card">
        <h1>Pay2Pee Terms of Service</h1>
        <p className="legal-meta">
          Last updated: {new Date().toLocaleDateString()}
        </p>

        <p>
          Welcome to <strong>Pay2Pee</strong>, provided by Pay2Pee LLC
          (&quot;we,&quot; &quot;us,&quot; &quot;our&quot;). These Terms of
          Service (&quot;Terms&quot;) govern your use of the Pay2Pee mobile app
          and related services.
        </p>

        <p>
          By creating an account or using Pay2Pee, you agree to be bound by
          these Terms, our Privacy Policy, Refund &amp; Cancellation Policy, and
          End User License Agreement (EULA). If you do not agree, please do not
          use the app.
        </p>

        <p>
          Website:{" "}
          <a
            href="https://pay2pee.app"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://pay2pee.app
          </a>
          <br />
          Email: <a href="mailto:support@pay2pee.app">support@pay2pee.app</a>
        </p>

        <h2>1. Overview of the Service</h2>
        <p>
          Pay2Pee is a location-based platform that connects guests who need
          restrooms (&quot;Guests&quot;) with participating businesses and
          individuals that offer restroom access for a fee (&quot;Hosts&quot;).
        </p>
        <p>
          We provide technology, not restrooms. Hosts are solely responsible for
          the restrooms and facilities they provide.
        </p>

        <h2>2. Eligibility</h2>
        <ul>
          <li>You must be at least 18 years old to use Pay2Pee.</li>
          <li>You must create an account with accurate information.</li>
          <li>You must use a valid, verifiable email address.</li>
        </ul>

        <h2>3. User Accounts</h2>
        <h3>3.1 Registration</h3>
        <p>
          You agree to provide accurate, complete information during signup and
          to keep it up to date.
        </p>

        <h3>3.2 Email Verification</h3>
        <p>
          You may be required to verify your email address before accessing the
          app. Accounts with unverified emails may be limited or blocked.
        </p>

        <h3>3.3 Security</h3>
        <p>
          You are responsible for maintaining the confidentiality of your login
          credentials. If you suspect unauthorized use of your account, notify
          us immediately.
        </p>

        <h3>3.4 Termination</h3>
        <p>
          We may suspend or terminate your account at any time if we believe:
        </p>
        <ul>
          <li>You violated these Terms or other policies</li>
          <li>You engaged in fraudulent, abusive, or unsafe behavior</li>
          <li>Your use of the app poses a risk to others or the platform</li>
        </ul>

        <h2>4. Payments, Fees &amp; Payouts</h2>
        <h3>4.1 Guest Payments</h3>
        <p>
          Guests may purchase single-visit passes, weekly passes, monthly
          passes, or other offers. Pricing may vary by host and location.
        </p>

        <h3>4.2 Payment Processing</h3>
        <p>
          Payments are processed securely by <strong>Stripe</strong>. Pay2Pee
          does not store full credit card numbers.
        </p>

        <h3>4.3 Platform Fees</h3>
        <p>
          Pay2Pee charges a platform fee (for example, 30%) on transactions.
          Fees and revenue shares may be updated over time.
        </p>

        <h3>4.4 Host Payouts</h3>
        <p>
          Host payouts are managed through <strong>Stripe Connect</strong>.
          Hosts are responsible for setting up and maintaining their payout
          details and complying with Stripe&apos;s terms.
        </p>

        <h3>4.5 Taxes</h3>
        <p>
          Hosts are responsible for any taxes, reporting, or obligations
          associated with revenue earned through Pay2Pee.
        </p>

        <h2>5. Location Services</h2>
        <p>
          Pay2Pee uses your location (when allowed) to show nearby restrooms and
          improve search accuracy. You can disable location permissions in your
          device settings, but some features may not work properly.
        </p>

        <h2>6. User Conduct</h2>
        <h3>6.1 Guest Responsibilities</h3>
        <ul>
          <li>Respect host properties and staff.</li>
          <li>Use restrooms for lawful purposes only.</li>
          <li>Follow any posted rules or instructions from hosts.</li>
          <li>Do not damage facilities or engage in unsafe behavior.</li>
        </ul>

        <h3>6.2 Host Responsibilities</h3>
        <ul>
          <li>Provide a functional, reasonably clean restroom.</li>
          <li>Honor reservations and visits unless truly unavailable.</li>
          <li>Maintain a safe environment for guests.</li>
          <li>Provide accurate listing information and pricing.</li>
        </ul>

        <h3>6.3 Prohibited Uses</h3>
        <p>You may not:</p>
        <ul>
          <li>Use the platform for illegal activities</li>
          <li>Attempt to bypass payments or access controls</li>
          <li>Harass, threaten, or harm other users</li>
          <li>Reverse-engineer, copy, or resell the app or its content</li>
        </ul>

        <h2>7. Intellectual Property</h2>
        <p>
          All Pay2Pee branding, logos, designs, text, graphics, and software are
          owned by Pay2Pee LLC and are protected by copyright, trademark, and
          other laws. You may not use our intellectual property without
          permission.
        </p>

        <h2>8. Disclaimers</h2>
        <p>Pay2Pee is provided on an &quot;AS IS&quot; and &quot;AS AVAILABLE&quot; basis.</p>
        <p>We do not guarantee that:</p>
        <ul>
          <li>Restrooms are always available or exactly as described</li>
          <li>The app will be error-free or uninterrupted</li>
          <li>Every host or guest will behave appropriately</li>
        </ul>

        <h2>9. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by law, Pay2Pee LLC is not responsible
          for:
        </p>
        <ul>
          <li>Conditions, cleanliness, or safety of restrooms</li>
          <li>Actions or omissions of hosts or guests</li>
          <li>Injuries, damages, or losses occurring at host locations</li>
          <li>Indirect, incidental, or consequential damages</li>
        </ul>

        <h2>10. Disputes &amp; Support</h2>
        <p>
          For disputes, concerns, or support, please contact us at{" "}
          <a href="mailto:support@pay2pee.app">support@pay2pee.app</a>. We
          strive to resolve issues fairly and promptly.
        </p>

        <h2>11. Changes to These Terms</h2>
        <p>
          We may update these Terms from time to time. When we do, we will
          update the &quot;Last updated&quot; date. Your continued use of the
          app means you accept the updated Terms.
        </p>
      </div>
    </div>
  );
}
